import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useAppSelector, useAppDispatch } from "./hooks/redux";
import {
  setUserPremium,
  setCityTag,
  setCentrifugoConnectionToken,
} from "./store/slices/mapSlice";
import {
  setSelectedCity,
  setEvents,
  setWebcams,
} from "./store/slices/mapSlice";
import { apiClient, setUrlInitData } from "./api/client";
import { useApiRequest } from "./api";
import type { City, TabType } from "./types";

// Новые хуки
import { useUrlParams, clearUrlParams } from "./hooks/useUrlParams";
import { useModalsState } from "./hooks/useModalsState";
import { useAppInitialization } from "./hooks/useAppInitialization";
import { useTheme } from "./hooks/useTheme";
import { useCentrifugo } from "./hooks/websocket/useCentrifugo";

// Компоненты - используем default imports
import { LoadingScreen } from "./components/LoadingScreen/LoadingScreen";
import { Map } from "./components/Map/Map";
import { TabManager } from "./components/TabManager/TabManager";
import { ModalsRenderer } from "./components/ModalsRenderer/ModalsRenderer";
import { CitySelector } from "./components/CitySelector/CitySelector";
import styles from "./App.module.css";
import { Paywall } from "./components/PayWall/PayWall";
//import { getPaywallType } from "./utils/abTest";
import { LocationClarification } from "./components/LocationClarification/LocationClarification";
import { Toast } from "./components/Toast/Toast";
import { YandexMetrika } from "./utils/yandexMetrika";
import { useWarmMap } from "./hooks/map/useWarmMap";
// import { AdvertisingBanner } from "./components/advertisingBanner";

export const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedCity, webcams, centrifugoConnectionToken, cityTag, mapToken, userData } =
    useAppSelector((state) => state.map);
  const networkingProfile = useAppSelector((state) => state.networking.profile);
  const { theme } = useTheme();
  const { request } = useApiRequest();

  const {
    warmMapRef,
    isWarmMapReady,
    warmMapInstance,
    initWarmMap,
    moveWarmMapToContainer,
    resetWarmMap,
  } = useWarmMap();

  // URL параметры
  const urlParams = useUrlParams();

  const isScreenMode = urlParams.screen === "map";
  // Состояние модальных окон
  const modals = useModalsState();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initData = params.get("x-telegram-init-data");
    if (initData) {
      setUrlInitData(initData);
    }
  }, []);

  // Инициализация приложения
  const {
    userPremium,
    contestData,
    isAppReady,
    setContestData,
    loadAppData,
    abTests,
    tariff,
    isEventReportsEnabled,
  } = useAppInitialization();

  const { isConnected } = useCentrifugo({
    connectionToken: centrifugoConnectionToken || "",
    cityTag: cityTag || "",
    isPushEnabled: selectedCity?.settings?.is_push_enabled !== false,
  });

  void isConnected;
  // Локальные состояния
  const [loaderCompleted, setLoaderCompleted] = useState(false);
  const [_showMapContainer, setShowMapContainer] = useState(false);
  const [activeTab, _setActiveTab] = useState<TabType>("map");
  const [cloudPaymentsKey] = useState(
    import.meta.env.VITE_CLOUDPAYMENTS_PUBLIC_KEY || ""
  );
  const [activePaywall, setActivePaywall] = useState(false);
  const [showLocationClarification, setShowLocationClarification] =
    useState(false);
  const [clarificationData, setClarificationData] = useState<{
    title: string;
    coordinates: [number, number];
    initialCoordinates?: [number, number];
    eventId?: number;
    locationId?: number;
    markerType?: "dps" | "clean" | "dtp";
  } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

  const handleOpenLocationClarification = useCallback(
    (data: {
      title: string;
      coordinates: [number, number];
      initialCoordinates?: [number, number];
      eventId?: number;
      locationId?: number;
    }) => {
      console.log("🎯 Открываем экран уточнения локации в App");
      console.log("📍 Координаты:", data.coordinates);
      console.log("📍 Исходные координаты:", data.initialCoordinates);
      console.log("🆔 ID события:", data.eventId);
      console.log("📦 Данные для LocationClarification:", data);
      setClarificationData(data);
      setShowLocationClarification(true);
    },
    []
  );

  const handleShowToast = useCallback((message: string = "Спасибо!") => {
    console.log("📢 Показываем Toast:", message);

    // Очищаем предыдущий таймер если есть
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setShowToast(true);

    // Скрываем через 1 секунду
    toastTimerRef.current = setTimeout(() => {
      setShowToast(false);
      toastTimerRef.current = null;
    }, 1000);
  }, []);

  const handleLocationClarificationFromSummary = useCallback(
    (data: { event: any; title: string; coordinates: [number, number]; initialCoordinates?: [number, number] }) => {
      console.log("🎯 App: Получили запрос уточнения локации из сводки", data);
      setClarificationData({
        title: data.title,
        coordinates: data.coordinates,
        initialCoordinates: data.initialCoordinates,
      });
      setShowLocationClarification(true);
    },
    []
  );

  // Обработчик закрытия экрана уточнения
  const handleCloseLocationClarification = useCallback(() => {
    console.log("❌ Закрываем экран уточнения локации");
    setShowLocationClarification(false);
    setClarificationData(null);
  }, []);

  const handleAddMarkerRequest = useCallback(
    (type: "dps" | "clean" | "dtp", coordinates: [number, number]) => {
      console.log("🆕 Запрос на добавление новой метки:", {
        type,
        coordinates,
      });

      setClarificationData({
        title: `Добавить ${
          type === "dps" ? "ДПС" : type === "clean" ? "Чисто" : "ДТП"
        }`,
        coordinates: coordinates,
        markerType: type,
      });
      setShowLocationClarification(true);
    },
    []
  );

  // Telegram WebApp инициализация
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;

      if (tg.disableVerticalSwipes) {
        tg.disableVerticalSwipes();
        console.log("✅ Вертикальные свайпы отключены");
      }

      setTimeout(() => {
        tg.ready();
        tg.expand();
        if (tg.requestFullscreen) {
          tg.requestFullscreen();
        }
      }, 100);

      try {
        if (typeof tg.disableClosingConfirmation === "function") {
          tg.disableClosingConfirmation();
          console.log("✅ Подтверждение закрытия отключено");
        }
      } catch (error) {
        console.warn("disableClosingConfirmation не поддерживается:", error);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // Viewport height управление
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;

      const updateViewportHeight = () => {
        const height =
          tg.viewportStableHeight || tg.viewportHeight || window.innerHeight;
        document.documentElement.style.setProperty(
          "--tg-viewport-height",
          `${height}px`
        );
      };

      updateViewportHeight();
      tg.onEvent("viewportChanged", updateViewportHeight);

      return () => {
        tg.offEvent("viewportChanged", updateViewportHeight);
      };
    }
  }, []);

  // Инициализация приложения на основе URL параметров
  useEffect(() => {
    const initializeApp = async () => {
      let data = null;

      if (urlParams.screen === "map") {
        console.log("📱 Открываем в режиме только карты");
        data = await loadAppData("initialization");
        // Не показываем никакие модалки в этом режиме
        return;
      }
      if (urlParams.summaryMode) {
        console.log("🔗 Открываем полноэкранную сводку");
        YandexMetrika.goToSvodka();
        modals.setShowFullscreenSummary(true);
        data = await loadAppData("initialization");
      } else if (urlParams.openSettings) {
        console.log("🔗 Открываем настройки");
        YandexMetrika.goToSettings();
        modals.setShowDirectSettings(true);
        data = await loadAppData("initialization");
      } else if (urlParams.startParam) {
        console.log("🔗 Обрабатываем start параметр:", urlParams.startParam);

        if (urlParams.startParam.startsWith("contest_prize_")) {
          console.log("🎁 Открываем призовой фонд");
          YandexMetrika.goToContest();
          modals.setShowFullscreenContest(true);
        } else if (urlParams.startParam.startsWith("contest_")) {
          console.log("🏆 Открываем конкурс");
          YandexMetrika.goToContest();
          modals.setShowFullscreenContest(true);
        } else if (urlParams.startParam.includes("flowers")) {
          console.log("🌸 Открываем страницу цветов");
          YandexMetrika.goToFlowers();
          modals.setShowFlowers(true);
        } else if (urlParams.startParam.includes("networking")) {
          console.log("🤝 Открываем страницу нетворкинга");
          YandexMetrika.goToNetworking();
          modals.setShowNetworking(true);
        } else if (urlParams.startParam.includes("gift_subscription")) {
          console.log("🎁 Открываем дарение подписки");
          modals.setShowGiftSubscription(true);
        } else if (urlParams.startParam.startsWith("ugift_")) {
          const giftCode = urlParams.startParam.replace("ugift_", "");
          console.log("🎁 Открываем активацию подарка, код:", giftCode);
          modals.setActivateGiftCode(giftCode);
          modals.setShowActivateGift(true);
        }

        data = await loadAppData("initialization");

        if (data?.shouldShowCitySelector) {
          modals.setShowCitySelector(true);
        }
      } else {
        data = await loadAppData("initialization");

        if (data?.shouldShowCitySelector) {
          modals.setShowCitySelector(true);
        }
      }

      // Показываем модальное окно рейтинга через 10 секунд
      if (data?.shouldShowRatingModal) {
        setTimeout(() => {
          modals.setShowRatingModal(true);
        }, 10000);
      }
    };

    initializeApp();
  }, [urlParams, loadAppData]);

  useEffect(() => {
    console.log("👤 userPremium изменился:", userPremium);

    const hasStartParams =
      urlParams.summaryMode === "fullscreen" ||
      urlParams.openSettings ||
      urlParams.startParam;

    if (
      userPremium === false &&
      !hasStartParams &&
      !modals.showFlowers &&
      isAppReady
    ) {
      setActivePaywall(true);
      console.log("💰 Показываем пейвол - обычный запуск без премиума");
    } else if (userPremium === true) {
      setActivePaywall(false);
      console.log("✅ Скрываем пейвол - есть премиум");
    } else if (hasStartParams || modals.showFlowers) {
      setActivePaywall(false);
      console.log("🔗 Не показываем пейвол - переход по стартовой ссылке");
    }
  }, [userPremium, isAppReady, urlParams, modals.showFlowers]);

  // Обработчики событий
  const handleSubscriptionSuccess = useCallback(() => {
    dispatch(setUserPremium(true));
    loadAppData("city-change");
  }, [loadAppData, dispatch]);

  const handleGiftActivationSuccess = useCallback(() => {
    console.log("🎁 Подарок активирован, перезагружаем данные приложения");
    dispatch(setUserPremium(true));
    loadAppData("city-change");
  }, [loadAppData, dispatch]);

  const handleMapReady = useCallback(() => {
    console.log("🗺️ Карта полностью загружена");
    // Карта всегда видимая, лоадер просто поверх неё
    setShowMapContainer(true);

    // Инициализируем warm map для LocationClarification
    if (selectedCity && mapToken && !isWarmMapReady) {
      console.log("🔥 Инициализация warm map для быстрого открытия");
      initWarmMap(selectedCity, mapToken, theme);
    }
  }, [selectedCity, mapToken, isWarmMapReady, initWarmMap, theme]);

  const handleLoaderComplete = useCallback(() => {
    setLoaderCompleted(true);
    // Убираем лоадер сразу - карта уже готова под ним
  }, []);

  const handleCitySelect = useCallback(
    async (city: City) => {
      console.log("🏙️ Выбран город:", city.name, "ID:", city.id);
      dispatch(setCityTag(""));
      dispatch(setCentrifugoConnectionToken(""));
      // dispatch(setEvents([]));
      // dispatch(setWebcams([]));
      dispatch(setSelectedCity(city));
      modals.setShowCitySelector(false);
      console.log("🏙️ [APP] ПОСЛЕ setSelectedCity:", {
        citySet: city.name,
        cityId: city.id,
      });
      try {
        await request(() => apiClient.saveCity(city.id));
        console.log("✅ Город сохранен на сервере");

        const data = await loadAppData("city-change");

        if (data) {
          const targetCity = data._cities?.find((c) => c.id === city.id);

          if (data) {
            dispatch(
              setEvents({
                events: data.events || [],
                isMailingEnabled:
                  targetCity?.settings?.is_mailing_enabled !== false,
              })
            );
            dispatch(setWebcams(data.webcams || []));
          }
          dispatch(setWebcams(data.webcams || []));
          setContestData(data.contest || null);
          console.log("✅ Данные для нового города загружены:", {
            events: data.events?.length || 0,
            webcams: data.webcams?.length || 0,
          });
        } else {
          console.log("⚠️ Данные не загрузились, оставляем пустые массивы");
          setContestData(null);
        }
      } catch (error) {
        console.error("❌ Ошибка при смене города:", error);
        dispatch(setEvents({ events: [], isMailingEnabled: false }));
        dispatch(setWebcams([]));
        setContestData(null);
      }
    },
    [dispatch, request, loadAppData, setContestData, modals]
  );

  const handleTabChange = useCallback(
    (tabId: TabType) => {
      modals.setSelectedWebcam(null);

      if (tabId === "summary") {
        YandexMetrika.goToSvodka();
        modals.setActiveModal("summary");
      } else if (tabId === "webcams") {
        modals.setActiveModal("webcams");
      } else if (tabId === "contest") {
        YandexMetrika.goToContest();
        modals.setShowFullscreenContest(true);
      } else if (tabId === "profile") {
        YandexMetrika.goToProfile();
        modals.setShowProfile(true);
      } else if (tabId === "flowers") {
        YandexMetrika.goToFlowers();
        modals.setShowFlowers(true);
      } else if (tabId === "networking") {
        YandexMetrika.goToNetworking();
        // Показываем NetworkingBanner если нет профиля, иначе открываем Networking
        if (networkingProfile) {
          modals.setShowNetworking(true);
        } else {
          modals.setShowNetworkingBanner(true);
        }
      } else if (tabId === "gift") {
        YandexMetrika.goToGift();
        modals.setShowGiftSubscription(true);
      }
    },
    [modals, networkingProfile, userPremium]
  );

  const handleWebcamSelect = useCallback(
    (webcam: any) => {
      modals.setSelectedWebcam(webcam);
      modals.setActiveModal("webcams");
    },
    [modals]
  );

  const handleModeSelect = useCallback((modeId: string) => {
    console.log("Выбран режим:", modeId);
  }, []);

  const handleZoomChange = useCallback((direction: "in" | "out") => {
    console.log("Изменение зума:", direction);
  }, []);

  const handleOpenMapFromSummary = useCallback(() => {
    console.log("🗺️ Переход к обычному режиму");
    modals.setShowFullscreenSummary(false);
    clearUrlParams();
  }, [modals]);

  const handleCloseDirectSettings = useCallback(() => {
    console.log("⚙️ Закрываем прямые настройки");
    modals.setShowDirectSettings(false);
    clearUrlParams();
  }, [modals]);

  const handleChangeCity = useCallback(() => {
    modals.setShowSettings(true);
  }, [modals]);

  const handleCitySelectorClose = useCallback(() => {
    if (selectedCity) {
      modals.setShowCitySelector(false);
    }
  }, [selectedCity, modals]);

  // Показать лоадер если приложение не готово
  const showLoader = !loaderCompleted || !isAppReady;

  return (
    <div className={styles.app}>
      {showLoader && <LoadingScreen onComplete={handleLoaderComplete} />}
      {/* (только для разработки)
      {!modals.showCitySelector && selectedCity && (
        <div
          style={{
            position: "fixed",
            top: 10,
            right: 10,
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: isConnected ? "#00ff00" : "#ff0000",
            zIndex: 9999,
          }}
        />
      )} */}
      {/* Селектор города */}
      {modals.showCitySelector && (
        <CitySelector
          selectedCity={selectedCity}
          isOpen={modals.showCitySelector}
          onCitySelect={handleCitySelect}
          onClose={handleCitySelectorClose}
          loading={false}
        />
      )}
      {/* Карта - всегда видимая, лоадер поверх неё */}
      {!modals.showCitySelector && selectedCity && (
        <div className={styles.mapContainer}>
          <Map
            onMapReady={handleMapReady}
            isPremium={userPremium}
            userId={telegramUser?.id?.toString()}
            cloudPaymentsPublicKey={cloudPaymentsKey}
            onSubscriptionSuccess={handleSubscriptionSuccess}
            activedModal={setActivePaywall}
            abTests={abTests}
            citySettings={selectedCity.settings}
            contestData={contestData}
            onContestClick={() => modals.setShowFullscreenContest(true)}
            onGiftClick={() => modals.setShowGiftSubscription(true)}
            onProfileClick={() => modals.setShowProfile(true)}
            userAvatar={userData?.photo_url}
            userName={userData?.name}
            onLocationClarificationRequest={handleOpenLocationClarification}
            onShowToast={handleShowToast}
            isScreenMode={isScreenMode}
            initialZoom={urlParams.zoom ?? undefined}
            onAddMarkerRequest={handleAddMarkerRequest}
            isEventReportsEnabled={isEventReportsEnabled}
          />
        </div>
      )}
      {selectedCity &&
        !showLoader &&
        !showLocationClarification &&
        !isScreenMode && (
          <TabManager
            webcamsCount={webcams.length}
            contestData={contestData}
            telegramUser={telegramUser}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            theme={theme}
            citySettings={selectedCity.settings}
          />
        )}

      {selectedCity && !isScreenMode && (
        <ModalsRenderer
          modalsState={modals}
          modalsActions={modals}
          webcams={webcams}
          contestData={contestData}
          telegramUser={telegramUser}
          userPremium={userPremium}
          onModeSelect={handleModeSelect}
          onZoomChange={handleZoomChange}
          onWebcamSelect={handleWebcamSelect}
          onChangeCity={handleChangeCity}
          onCitySelect={handleCitySelect}
          onOpenMapFromSummary={handleOpenMapFromSummary}
          onCloseDirectSettings={handleCloseDirectSettings}
          urlParams={urlParams}
          citySettings={selectedCity.settings}
          onLocationClarificationRequest={
            handleLocationClarificationFromSummary
          }
          onGiftActivationSuccess={handleGiftActivationSuccess}
        />
      )}

      {/* ТЕСТОВЫЙ НОВЫЙ ПЕЙВОЛ */}
      {/* getPaywallType(abTests) === "B" && */}
      {activePaywall && !showLoader && (
        <Paywall
          isOpen={activePaywall}
          onClose={() => setActivePaywall(false)}
          onSubscriptionSuccess={handleSubscriptionSuccess}
          cloudPaymentsPublicKey={cloudPaymentsKey}
          userId={telegramUser?.id?.toString()!}
          tariff={tariff}
        />
      )}
      <LocationClarification
        isOpen={showLocationClarification}
        onClose={handleCloseLocationClarification}
        markerData={clarificationData}
        warmMapRef={warmMapRef}
        warmMapInstance={warmMapInstance}
        isWarmMapReady={isWarmMapReady}
        onMoveWarmMapToContainer={moveWarmMapToContainer}
        onResetWarmMap={resetWarmMap}
      />
      <Toast isVisible={showToast} />

      {/* Временно отключен рекламный баннер */}
      {/* <AdvertisingBanner
        onBannerClick={(type) => {
          console.log("🎁 Клик по баннеру:", type);
          modals.setShowGiftSubscription(true);
        }}
      /> */}

      {/* Offscreen контейнер для warm map */}
      <div
        id="warm-map-offscreen"
        style={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "100%",
          height: "100vh",
          visibility: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          ref={warmMapRef}
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      </div>
    </div>
  );
};
